// src/components/connections/SugestoesAside.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { sugerirSocios as sugerirSociosApi } from '../../services/sugestoesApi';
import { useAuth } from '../../context/AuthContext';

export default function SugestoesAside({
  /** opcional: injeta itens prontos e pula o fetch */
  items,
  /** título do bloco */
  title = 'Sugestões',
  /** rótulo padrão do botão principal */
  actionLabel = 'Convidar',
  /** uid do usuário; se não vier, usa AuthContext */
  uid,
  /** quantidade pedida ao backend */
  limit = 6,
  /** permite sobrepor o fetcher (para testes) */
  fetcher = sugerirSociosApi,
  /** callback chamada ao clicar em Convidar (recebe user); deve retornar uma Promise */
  onConvidar,
  /** callback chamada ao clicar em Ignorar (recebe user); deve retornar uma Promise */
  onIgnorar,
  /** mostra skeletons enquanto carrega */
  skeleton = 4,
}) {
  const { usuario } = useAuth();
  const effectiveUid = uid || usuario?.uid;

  const [data, setData] = useState(() => (Array.isArray(items) ? items : []));
  const [loading, setLoading] = useState(!Array.isArray(items));
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null); // controla loading por item

  const shouldFetch = useMemo(
    () => !Array.isArray(items) && !!effectiveUid,
    [items, effectiveUid]
  );

  useEffect(() => {
    let mounted = true;
    if (!shouldFetch) return;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetcher(effectiveUid, limit);
        if (!mounted) return;

        // Normaliza campos esperados pelo componente
        const normalized = (res || []).map((u) => ({
          id: u.uid || u.id,
          nome: u.nome || u.name,
          info: u.info || u.especialidade || '',
          avatarUrl: u.avatarUrl || u.fotoPerfil?.url || u.fotoPerfil || null,
          profileHref:
            u.profileHref ||
            (u.uid ? `/perfil/${u.uid}` : u.id ? `/perfil/${u.id}` : undefined),
          reason: u.reason || '',
          score: typeof u.score === 'number' ? u.score : null,
        }));
        setData(normalized);
      } catch (e) {
        console.error('[SugestoesAside] erro no fetch:', e);
        setError('Não foi possível carregar sugestões agora.');
        setData([]);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, effectiveUid, limit]);

  const handleConvidar = async (user) => {
    if (!onConvidar) return;
    try {
      setBusyId(user.id);
      await onConvidar(user);
      // remoção otimista
      setData((prev) => prev.filter((x) => x.id !== user.id));
    } catch (e) {
      console.error('Falha ao convidar:', e);
      alert('Não foi possível enviar o convite. Tente novamente.');
    } finally {
      setBusyId(null);
    }
  };

  const handleIgnorar = async (user) => {
    try {
      setBusyId(user.id);
      // se veio callback externa, chama — ex: gravar user_blocks
      if (onIgnorar) {
        await onIgnorar(user);
      }
      // remoção local (pelo menos não aparece mais na sessão)
      setData((prev) => prev.filter((x) => x.id !== user.id));
    } catch (e) {
      console.error('Falha ao ignorar:', e);
      alert('Não foi possível ocultar esta sugestão.');
    } finally {
      setBusyId(null);
    }
  };

  // Loading (skeletons)
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 p-4 rounded-lg space-y-3">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {Array.from({ length: skeleton }).map((_, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
              <div className="space-y-1">
                <div className="w-36 h-3 bg-gray-200 rounded animate-pulse" />
                <div className="w-24 h-3 bg-gray-100 rounded animate-pulse" />
              </div>
            </div>
            <div className="w-24 h-8 bg-gray-200 rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  // Erro
  if (error) {
    return (
      <div className="bg-white border border-red-200 p-4 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  // Sem itens
  if (!data?.length) {
    return (
      <div className="bg-white border border-gray-200 p-4 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500">Sem sugestões no momento.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 p-4 rounded-lg space-y-3">
      <h3 className="font-semibold text-gray-900">{title}</h3>

      {data.map((u) => (
        <div key={u.id} className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={u.avatarUrl || '/placeholder-user.jpg'}
              alt=""
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              loading="lazy"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {u.nome}
              </p>
              <div className="text-[11px] text-gray-500 truncate">
                {u.info}
                {u.reason ? (u.info ? ' • ' : '') + u.reason : ''}
                {typeof u.score === 'number' ? ` • Score ${u.score}` : ''}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <a
              href={u.profileHref || '#'}
              className="text-xs text-gray-700 hover:underline"
              target="_self"
              rel="noreferrer"
              onClick={(e) => {
                if (!u.profileHref) e.preventDefault();
              }}
            >
              Ver perfil
            </a>

            <button
              disabled={busyId === u.id}
              onClick={() => handleConvidar(u)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm disabled:opacity-60"
              title="Convidar para sociedade"
            >
              {busyId === u.id ? 'Enviando...' : actionLabel}
            </button>

            <button
              disabled={busyId === u.id}
              onClick={() => handleIgnorar(u)}
              className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-60"
              title="Não mostrar esta sugestão"
            >
              Ignorar
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
